/**
 * @file useUserCard.js
 * @description NavBar 用户卡片：导航至个人中心子模块、退出登录
 * @module features/user/composables/useUserCard
 */

import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { goHomeAfterLogout } from '@/shared/utils/logoutNavigation'
import { clearAuthSession, getAuthToken } from '@/shared/utils/auth'
import { isMobileLayoutViewport } from '@/shared/utils/mobileLayout'
import { signOutApi } from '@/features/user/api/userApi'
import { USER_CENTER_VALID_MODULES } from '@/features/user/constants/userCenterModules'

/** 旧模块别名 → 标准模块名 */
const MODULE_ALIAS_MAP = {
  profile: 'UserBaseInfo',
  membership: 'Membership',
}

/**
 * @param {{ user: Record<string, unknown> }} props
 * @param {(event: 'hide' | 'logout') => void} emit
 */
export function useUserCard(props, emit) {
  const router = useRouter()
  const userStore = useUserStore()

  userStore.fetchUserInfo()

  const isMobile = ref(false)

  const goldAmount = computed(() => props.user?.userGoldInfo?.goldAmount ?? 0)
  const totalConsumeAmount = computed(
    () => props.user?.userGoldInfo?.totalConsumeAmount ?? 0
  )

  function updateIsMobile() {
    isMobile.value = isMobileLayoutViewport()
  }

  function hideCard() {
    emit('hide')
  }

  function mapModuleName(module) {
    return MODULE_ALIAS_MAP[module] || module
  }

  function navigateTo(module) {
    const mappedModule = mapModuleName(module)

    if (!USER_CENTER_VALID_MODULES.includes(mappedModule)) {
      console.warn(`模块 ${mappedModule} 不存在`)
      return
    }

    hideCard()
    if (isMobile.value) {
      router.push({
        name: 'UserModule',
        params: { module: mappedModule },
      })
    } else {
      router.push({
        name: 'UserCenter',
        query: { module: mappedModule },
      })
    }
  }

  function goToUserCenter() {
    navigateTo('UserBaseInfo')
  }

  async function handleLogout() {
    try {
      const token = getAuthToken()
      if (!token) {
        console.warn('用户未登录，无需退出')
        emit('logout')
        return
      }

      const response = await signOutApi()

      if (response.data.code === 200 || response.data.code === 0) {
        userStore.clearUser()
        clearAuthSession()

        hideCard()
        goHomeAfterLogout(router)
      } else {
        console.error('退出失败:', response.data.msg || '未知错误')
      }
    } catch (error) {
      console.error('退出请求失败:', error.response?.data || error.message)
    }
  }

  onMounted(() => {
    updateIsMobile()
    window.addEventListener('resize', updateIsMobile)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('resize', updateIsMobile)
  })

  return {
    goldAmount,
    totalConsumeAmount,
    hideCard,
    goToUserCenter,
    navigateTo,
    handleLogout,
  }
}
